import React from "react";
import Profile from "../models/Profile";
import LiveApiClient, { LiveApiCache } from "../api/LiveApiClient";
import IdentityClient from "../api/IdentityClient";
import { Cache } from "../util/Cache.util";

const AuthContext = React.createContext({
	token: undefined,
	currentUser: undefined,
	profile: undefined,
	organizationMember: undefined,
	setOrganizationMember: () => Promise,
	checkAuth: () => Promise,
	login: ({ username, password, pin, otp, mfa }, ignore_errors = false) => Promise,
	logout: () => Promise,
	updateCurrentUser: () => Promise,
	updateUserCatalogs: () => Promise,
	requestPin: () => Promise,
	clearStore: () => { },
	isSuperAdmin: () => { },
	completeRegistration: ({ username, password, pin, organization }) => {},
});

export const useAuth = () => {
	const context = React.useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within a AuthProvider");
	}
	return context;
};

export const AuthProvider = ({ children }) => {
	const storedToken = undefined;
	const [token, setToken] = React.useState(storedToken || undefined);

	const storedCurrentUser = JSON.parse(localStorage.getItem("currentUser"));
	const [currentUser, setCurrentUser] = React.useState(storedCurrentUser || undefined);

	// Grab directly from identity server
	if (currentUser && !currentUser?.email_verified) {
		IdentityClient.post("getToken",).then(tokenResponse => {
			IdentityClient.get('identity/identity', { params: { token: tokenResponse.data.token } })
				.then(({ data }) => {
					if (+data.email_verified) {
						currentUser.email_verified = true;
						setCurrentUser(currentUser);
						localStorage.setItem("currentUser", JSON.stringify(currentUser));
					}
				})
		});
	}

	//profile is never stored into localStorage anywhere
	const storedProfile = new Profile(JSON.parse(localStorage.getItem("profile")));
	const [profile] = React.useState(storedProfile || undefined);

	const [organizationMember, setOrganizationMember] = React.useState(undefined);

	const requestPin = async username => {
		const { data } = await IdentityClient.post("otp-login-request", { username });
		return data;
	};

	const checkAuth = async (auth) => {
		return new Promise(async (resolve, reject) => {
			const response = await IdentityClient.post("authorize", { 'auth-token': auth });
			if (!response.data.identity) {
				reject(new Error("Invalid auth token"));
				return;
			}

			LiveApiCache.Set(auth);
			// await LiveApiCache.Set(auth);
			const token = { token: auth, expires_on: +response.data.expires };
			LiveApiCache.Get();
			setToken(token);

			const user = response.data.identity;
			delete user.meta_json;
			delete user.meta;

			localStorage.setItem("currentUser", JSON.stringify(user));
			setCurrentUser(user);
			resolve(response?.data);
			return;
		});
	};

	const login = async ({ username, password, pin, otp, mfa }, ignore_errors = false) => {
		const response = await IdentityClient.post("authorize", {
			username,
			password,
			pin,
			otp,
			mfa,
		});

		if (response.data.success && response.data.token && response.data.token !== "undefined") {
			setToken(response.data.token);
			const user = response.data.identity;
			delete user.meta_json;
			delete user.meta;

			setCurrentUser(user);
			localStorage.setItem("currentUser", JSON.stringify(user));
		} else if (!ignore_errors && response.data.errors) {
			logout(true);
		}
		return response?.data;
	};

	const getUserCatalogs = async () => {
		const paths = encodeURIComponent(JSON.stringify({
			id: true,
			label: true,
			title: true,
			uri: true,
			initial_uri: true,
			options: true,
			account: {
				id: true,
				name: true,
				logo: true,
			},
		}));
		const filter = encodeURIComponent(JSON.stringify([{
			"property": "{access.id}",
			"operator": "!=",
			"value": null
		}]));
		const response = await LiveApiClient.get(`Catalog?paths=${paths}&filter=${filter}`);
		return response.data;
	};

	const logout = async (skipRedirect = false) => {
		await IdentityClient.post("deleteToken");
		LiveApiCache.Reset();
		clearStore();
		// setXXX need to be after deleteToken and clearStore or it will appear to not work after reload
		setToken(undefined);
		if (!skipRedirect) {
			window.location.href = "/";
		}
		else {
			// this will cause a redirect from useEffect() in ProtectedLayout
			setCurrentUser(undefined);
		}
	}

	const clearStore = () => {
		localStorage.removeItem('catalog_invitation:data');
		localStorage.removeItem("currentUser");
		localStorage.removeItem("currentCatalog");
		localStorage.removeItem("profile");
		localStorage.removeItem("background");
		localStorage.removeItem("foreground");
		localStorage.removeItem("throttle");
		localStorage.removeItem("organization_member:data");
		localStorage.removeItem("customer");
		Cache.Local().set("has-b2c-catalog", undefined);
		sessionStorage.removeItem("buying_mode");
	}

	const updateCurrentUser = (userNewData) => {
		const updatedUser = Object.assign({}, currentUser, userNewData);
		setCurrentUser(updatedUser);
		localStorage.setItem("currentUser", JSON.stringify(updatedUser));
	};
	const updateUserCatalogs = async () => {
		const user = currentUser;
		user.catalogs = await getUserCatalogs();
		setCurrentUser(user);
		localStorage.setItem("currentUser", JSON.stringify(user));
	};
	
	const isSuperAdmin = () => {
		return organizationMember?.profile?.email?.endsWith("@bosslogics.com");
	}
	
	const completeRegistration = async ({ username, password, pin, organization }) => {
		return await IdentityClient.post(
			"complete-registration",
			{
				username, password, pin, organization,
			},
		).then(r => r.data);
	}

	const value = {
		token,
		currentUser,
		profile,
		organizationMember,
		setOrganizationMember,
		checkAuth,
		login,
		logout,
		updateCurrentUser,
		updateUserCatalogs,
		requestPin,
		clearStore,
		isSuperAdmin,
		completeRegistration,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
